import { Box, CircularProgress } from '@mui/material';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TaskService } from 'service/task.service';
import zeplinColor from 'theme/Color';
import { TaskFilters } from '../TasksFilters/TasksFilters';
import { AddTaskForm } from '../TasksFilters/components/AddTaskForm';
import { TaskTypeSelectionModal } from '../TasksFilters/components/TaskTypeSelectionModal/TaskTypeSelectionModal';
import { TasksKanbanColumn } from './TasksKanbanColumn';

type TaskType = 'TODO' | 'IN_PROGRESS' | 'DONE';

type TaskData = {
  totalRecords: number;
  records: any[];
  totalFilteredRecords: number;
};

type TasksData = {
  toDo: TaskData;
  inProgress: TaskData;
  done: TaskData;
};

type TasksKanbanProps = {
  mine?: boolean;
  filters?: TaskFilters;
  tasksData?: TasksData;
  loading?: boolean;
  onRefresh?: () => void;
};

export const TasksKanban = ({
  mine = false,
  filters,
  tasksData: externalTasksData,
  loading: externalLoading = false,
  onRefresh,
}: TasksKanbanProps) => {
  const params = useParams();
  const { alertState, completeOperation, setAlertState, getAlertContent } = useAlertStatus();
  const onRefreshRef = useRef(onRefresh);
  const filtersRef = useRef(filters);
  const prevFiltersStringRef = useRef<string>('');

  useEffect(() => {
    onRefreshRef.current = onRefresh;
    filtersRef.current = filters;
  }, [onRefresh, filters]);

  const [action, setAction] = useState('');
  const [edit, setEdit] = useState(false);
  const [added, setAdded] = useState(false);

  const [toDoData, setToDoData] = useState<TaskData>(
    externalTasksData?.toDo || {
      totalRecords: 0,
      records: [],
      totalFilteredRecords: 0,
    },
  );

  const [inProgressData, setInProgressData] = useState<TaskData>(
    externalTasksData?.inProgress || {
      totalRecords: 0,
      records: [],
      totalFilteredRecords: 0,
    },
  );

  const [doneData, setDoneData] = useState<TaskData>(
    externalTasksData?.done || {
      totalRecords: 0,
      records: [],
      totalFilteredRecords: 0,
    },
  );

  const [toDoLoading, setToDoLoading] = useState(externalTasksData ? externalLoading : true);
  const [inProgressLoading, setInProgressLoading] = useState(
    externalTasksData ? externalLoading : true,
  );
  const [doneLoading, setDoneLoading] = useState(externalTasksData ? externalLoading : true);

  const [toDooffset, setToDoOffset] = useState(0);
  const [inProgressoffset, setInProgressOffset] = useState(0);
  const [doneoffset, setDoneOffset] = useState(0);
  const toDoOffsetRef = useRef(0);
  const inProgressOffsetRef = useRef(0);
  const doneOffsetRef = useRef(0);

  const [drawerState, setDrawerState] = useState(false);
  const [viewDrawerStateToDo, setViewDrawerStateToDo] = useState(false);
  const [viewDrawerStateInProgress, setViewDrawerStateInProgress] = useState(false);
  const [viewDrawerStateDone, setViewDrawerStateDone] = useState(false);
  const [initialTaskStatus, setInitialTaskStatus] = useState<string>('');
  const [taskTypeModalOpen, setTaskTypeModalOpen] = useState(false);
  const [selectedTaskType, setSelectedTaskType] = useState<string | null>(null);
  const statusRef = useRef<string>('');

  const taskConfig = {
    TODO: {
      apiStatus: 'To Do',
      loadingSetter: setToDoLoading,
      offsetSetter: setToDoOffset,
      dataSetter: setToDoData,
    },
    IN_PROGRESS: {
      apiStatus: 'in progress',
      loadingSetter: setInProgressLoading,
      offsetSetter: setInProgressOffset,
      dataSetter: setInProgressData,
    },
    DONE: {
      apiStatus: 'Done',
      loadingSetter: setDoneLoading,
      offsetSetter: setDoneOffset,
      dataSetter: setDoneData,
    },
  };

  useEffect(() => {
    if (externalTasksData) {
      setToDoData(externalTasksData.toDo);
      setInProgressData(externalTasksData.inProgress);
      setDoneData(externalTasksData.done);
      setToDoLoading(externalLoading);
      setInProgressLoading(externalLoading);
      setDoneLoading(externalLoading);
    }
  }, [externalTasksData, externalLoading]);

  useEffect(() => {
    toDoOffsetRef.current = toDooffset;
    inProgressOffsetRef.current = inProgressoffset;
    doneOffsetRef.current = doneoffset;
  }, [toDooffset, inProgressoffset, doneoffset]);

  const fetchTasks = useCallback(
    async (type: TaskType, next: boolean = false) => {
      if (externalTasksData && !next) {
        return;
      }

      const config = taskConfig[type];
      const currentOffset =
        type === 'TODO'
          ? toDoOffsetRef.current
          : type === 'IN_PROGRESS'
            ? inProgressOffsetRef.current
            : doneOffsetRef.current;

      config.loadingSetter(true);

      try {
        const queryParams: any = {
          mine: mine,
        };

        if (params?.id) {
          queryParams.client = params.id;
        }

        const currentFilters = filtersRef.current;
        if (currentFilters) {
          if (currentFilters.priority && currentFilters.priority.length > 0) {
            queryParams.priority = currentFilters.priority.join(',');
          }

          if (currentFilters.client && currentFilters.client.length > 0) {
            queryParams.client = currentFilters.client.join(',');
          }

          if (currentFilters.recordTypes && currentFilters.recordTypes.length > 0) {
            queryParams.recordType = currentFilters.recordTypes.join(',');
          }

          if (currentFilters.assignedTo && currentFilters.assignedTo.length > 0) {
            queryParams.assignedTo = currentFilters.assignedTo.join(',');
          }

          if (currentFilters.search && currentFilters.search.trim()) {
            queryParams.search = currentFilters.search.trim();
          }

          if (currentFilters.needAttention) {
            queryParams.needAttention = true;
          }

          if (currentFilters.time && currentFilters.time.length > 0) {
            queryParams.dueDate = currentFilters.time[0];
          }
        }

        const response = await TaskService.getKanban(queryParams);

        const statusKey =
          config.apiStatus === 'To Do'
            ? 'To Do'
            : config.apiStatus === 'in progress'
              ? 'In Progress'
              : 'Done';

        const tasks = response.data?.[statusKey] || [];

        const pageSize = 10;
        const paginatedTasks = next
          ? tasks.slice(currentOffset, currentOffset + pageSize)
          : tasks.slice(0, pageSize);

        const transformedResponse = {
          totalRecords: tasks.length,
          records: paginatedTasks,
          totalFilteredRecords: tasks.length,
        };

        if (paginatedTasks.length > 0 || !next) {
          config.dataSetter(
            next
              ? (prev: TaskData) => ({
                  ...prev,
                  ...transformedResponse,
                  records: [...prev.records, ...paginatedTasks],
                })
              : transformedResponse,
          );
          config.offsetSetter(next ? currentOffset + pageSize : pageSize);
        }
      } catch (err) {
        completeOperation('fetch', 'Something went wrong', true);
      } finally {
        config.loadingSetter(false);
      }
    },
    [externalTasksData, mine, params?.id],
  );

  const getToDoTaskRef = useRef<(next?: boolean) => Promise<void>>();
  const getInProgressTaskRef = useRef<(next?: boolean) => Promise<void>>();
  const getDoneTaskRef = useRef<(next?: boolean) => Promise<void>>();

  useEffect(() => {
    getToDoTaskRef.current = (next?: boolean) => fetchTasks('TODO', next);
    getInProgressTaskRef.current = (next?: boolean) => fetchTasks('IN_PROGRESS', next);
    getDoneTaskRef.current = (next?: boolean) => fetchTasks('DONE', next);
  }, [fetchTasks]);

  const getToDoTask = useCallback(async (next?: boolean) => {
    if (getToDoTaskRef.current) {
      await getToDoTaskRef.current(next);
    }
  }, []);
  const getInProgressTask = useCallback(async (next?: boolean) => {
    if (getInProgressTaskRef.current) {
      await getInProgressTaskRef.current(next);
    }
  }, []);
  const getDoneTask = useCallback(async (next?: boolean) => {
    if (getDoneTaskRef.current) {
      await getDoneTaskRef.current(next);
    }
  }, []);

  const handleStatusChange = useCallback(() => {
    getToDoTaskRef.current?.();
    getInProgressTaskRef.current?.();
    getDoneTaskRef.current?.();

    if (onRefreshRef.current) {
      onRefreshRef.current();
    }
  }, []);

  const getColumnTitleFromStatus = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'to do' || statusLower === 'todo') return 'To Do';
    if (statusLower === 'in progress' || statusLower === 'inprogress') return 'In Progress';
    if (statusLower === 'done') return 'Done';
    return status;
  };

  function handleOnDrag(e: React.DragEvent, taskId: string, sourceColumn: string) {
    e.dataTransfer?.setData('taskId', taskId);
    e.dataTransfer?.setData('sourceColumn', sourceColumn);
  }

  function handleOnDragOver(e: React.DragEvent) {
    e.preventDefault();
  }

  async function handleOnDrop(e: React.DragEvent, targetColumn: string) {
    const taskId = e.dataTransfer.getData('taskId');
    const sourceColumn = e.dataTransfer.getData('sourceColumn');

    if (!taskId) return;

    const targetStatus = targetColumn;
    const sourceStatus = getColumnTitleFromStatus(sourceColumn);

    if (sourceStatus === targetColumn) return;

    let taskToMove: any = null;

    if (sourceStatus === 'To Do') {
      taskToMove = toDoData.records.find((t: any) => t.id === taskId);
    } else if (sourceStatus === 'In Progress') {
      taskToMove = inProgressData.records.find((t: any) => t.id === taskId);
    } else if (sourceStatus === 'Done') {
      taskToMove = doneData.records.find((t: any) => t.id === taskId);
    }

    if (!taskToMove) return;

    const prevToDoData = toDoData;
    const prevInProgressData = inProgressData;
    const prevDoneData = doneData;
    if (sourceStatus === 'To Do') {
      setToDoData((prev) => ({
        ...prev,
        records: prev.records.filter((t: any) => t.id !== taskId),
        totalRecords: prev.totalRecords - 1,
        totalFilteredRecords: prev.totalFilteredRecords - 1,
      }));
    } else if (sourceStatus === 'In Progress') {
      setInProgressData((prev) => ({
        ...prev,
        records: prev.records.filter((t: any) => t.id !== taskId),
        totalRecords: prev.totalRecords - 1,
        totalFilteredRecords: prev.totalFilteredRecords - 1,
      }));
    } else if (sourceStatus === 'Done') {
      setDoneData((prev) => ({
        ...prev,
        records: prev.records.filter((t: any) => t.id !== taskId),
        totalRecords: prev.totalRecords - 1,
        totalFilteredRecords: prev.totalFilteredRecords - 1,
      }));
    }

    const updatedTask = { ...taskToMove, status: targetStatus };
    if (targetColumn === 'To Do') {
      setToDoData((prev) => ({
        ...prev,
        records: [...prev.records, updatedTask],
        totalRecords: prev.totalRecords + 1,
        totalFilteredRecords: prev.totalFilteredRecords + 1,
      }));
    } else if (targetColumn === 'In Progress') {
      setInProgressData((prev) => ({
        ...prev,
        records: [...prev.records, updatedTask],
        totalRecords: prev.totalRecords + 1,
        totalFilteredRecords: prev.totalFilteredRecords + 1,
      }));
    } else if (targetColumn === 'Done') {
      setDoneData((prev) => ({
        ...prev,
        records: [...prev.records, updatedTask],
        totalRecords: prev.totalRecords + 1,
        totalFilteredRecords: prev.totalFilteredRecords + 1,
      }));
    }

    try {
      await TaskService.updateTask(taskId, { status: targetStatus });
      handleStatusChange();
      completeOperation('update', 'Task status updated successfully');
    } catch (error: any) {
      setToDoData(prevToDoData);
      setInProgressData(prevInProgressData);
      setDoneData(prevDoneData);

      completeOperation(
        'update',
        error?.response?.data?.message || 'Failed to update task status',
        true,
      );
    }
  }

  const handleAddTaskFromColumn = (status: string) => {
    statusRef.current = status;
    setInitialTaskStatus(status);
    setTaskTypeModalOpen(true);
  };

  const handleTaskTypeSelect = (taskType: string) => {
    setSelectedTaskType(taskType);
    setTaskTypeModalOpen(false);
    if (!initialTaskStatus && statusRef.current) {
      setInitialTaskStatus(statusRef.current);
    }
    setDrawerState(true);
  };

  const handleTaskTypeModalClose = () => {
    setTaskTypeModalOpen(false);
    setInitialTaskStatus('');
  };

  useEffect(() => {
    const filtersString = JSON.stringify(filters);
    const filtersChanged = filtersString !== prevFiltersStringRef.current;

    if (filtersChanged) {
      prevFiltersStringRef.current = filtersString;
      if (!externalTasksData) {
        getToDoTaskRef.current?.();
        getInProgressTaskRef.current?.();
        getDoneTaskRef.current?.();
      } else if (onRefreshRef.current) {
        onRefreshRef.current();
      }
    }
  }, [filters, externalTasksData]);

  useEffect(() => {
    if (added) {
      if (!externalTasksData) {
        getToDoTaskRef.current?.();
        getInProgressTaskRef.current?.();
        getDoneTaskRef.current?.();
      } else if (onRefreshRef.current) {
        onRefreshRef.current();
      }
      completeOperation('create', 'Task added successfully');
      setAdded(false);
    }
  }, [added, externalTasksData, completeOperation]);

  useEffect(() => {
    if (edit) {
      if (!externalTasksData) {
        getToDoTaskRef.current?.();
        getInProgressTaskRef.current?.();
        getDoneTaskRef.current?.();
      } else if (onRefreshRef.current) {
        onRefreshRef.current();
      }
      completeOperation('update', 'Task updated successfully');
      setEdit(false);
    }
  }, [edit, externalTasksData, completeOperation]);

  useEffect(() => {
    if (action) {
      if (!externalTasksData) {
        getToDoTaskRef.current?.();
        getInProgressTaskRef.current?.();
        getDoneTaskRef.current?.();
      } else if (onRefreshRef.current) {
        onRefreshRef.current();
      }

      const isError =
        !action.includes('successfully') &&
        !action.includes('removed') &&
        !action.includes('moved');
      completeOperation('update', `Task ${action}`, isError);

      setAction('');
    }
    sessionStorage.removeItem('dealSelectedFromOverview');
  }, [action, externalTasksData]);

  useEffect(() => {
    if (!drawerState) {
      setInitialTaskStatus('');
      setSelectedTaskType(null);
    }
  }, [drawerState]);

  const isInitialLoading =
    externalTasksData && externalLoading
      ? externalLoading &&
        toDoData.records.length === 0 &&
        inProgressData.records.length === 0 &&
        doneData.records.length === 0
      : !externalTasksData &&
        toDoLoading &&
        inProgressLoading &&
        doneLoading &&
        toDoData.records.length === 0 &&
        inProgressData.records.length === 0 &&
        doneData.records.length === 0;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '40px',
        width: '100%',
        flex: 1,
        height: '100%',
        position: 'relative',
      }}
    >
      {isInitialLoading && externalTasksData && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 10,
            borderRadius: '14px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: zeplinColor.BasicBlack }} size={40} />
          </Box>
        </Box>
      )}

      {[
        {
          data: toDoData,
          loading: toDoLoading,
          getTask: getToDoTask,
          tabTitle: 'To Do',
          ellipse: 'to-do-ellipse.svg',
          viewDrawerState: viewDrawerStateToDo,
          setViewDrawerState: setViewDrawerStateToDo,
          status: 'To Do',
        },
        {
          data: inProgressData,
          loading: inProgressLoading,
          getTask: getInProgressTask,
          tabTitle: 'In Progress',
          ellipse: 'in-progress-ellipse.svg',
          viewDrawerState: viewDrawerStateInProgress,
          setViewDrawerState: setViewDrawerStateInProgress,
          status: 'In Progress',
        },
        {
          data: doneData,
          loading: doneLoading,
          getTask: getDoneTask,
          tabTitle: 'Done',
          ellipse: 'done-ellipse.svg',
          viewDrawerState: viewDrawerStateDone,
          setViewDrawerState: setViewDrawerStateDone,
          status: 'Done',
        },
      ].map((column) => (
        <Box key={column.tabTitle} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <TasksKanbanColumn
            data={column.data}
            loading={column.loading}
            getTask={column.getTask}
            tabTitle={column.tabTitle}
            ellipse={column.ellipse}
            setAction={setAction}
            setEdit={setEdit}
            setViewDrawerState={column.setViewDrawerState}
            viewDrawerState={column.viewDrawerState}
            onAddTask={() => handleAddTaskFromColumn(column.status)}
            onStatusChange={handleStatusChange}
            onDrag={handleOnDrag}
            onDragOver={handleOnDragOver}
            onDrop={handleOnDrop}
          />
        </Box>
      ))}

      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.isError}
      />

      <TaskTypeSelectionModal
        isModalOpen={taskTypeModalOpen}
        onClose={handleTaskTypeModalClose}
        onSelect={handleTaskTypeSelect}
      />

      {drawerState && selectedTaskType && (initialTaskStatus || statusRef.current) && (
        <AddTaskForm
          key={`add-task-${initialTaskStatus || statusRef.current}-${selectedTaskType}`}
          drawerState={drawerState}
          onClose={() => {
            setDrawerState(false);
            setSelectedTaskType(null);
            setInitialTaskStatus('');
            statusRef.current = '';
          }}
          taskType={selectedTaskType}
          initialStatus={initialTaskStatus || statusRef.current}
          onTaskAdded={() => {
            setAdded(true);
            if (externalTasksData && onRefresh) {
              onRefresh();
            } else {
              getToDoTask();
              getInProgressTask();
              getDoneTask();
            }
          }}
        />
      )}
    </Box>
  );
};
